// Reusable UI primitives matching design (flat surfaces, 1px borders, no shadows)
import { useEffect } from "react";
import { Plus, X } from "@phosphor-icons/react";

export function PageHeader({ title, subtitle, eyebrow, action }) {
    return (
        <div className="flex items-end justify-between gap-4 pb-6 border-b border-fleet-border mb-6">
            <div>
                {eyebrow && <div className="label-mono mb-2">{eyebrow}</div>}
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-fleet-ink">
                    {title}
                </h1>
                {subtitle && <p className="text-sm text-fleet-inkSoft mt-1">{subtitle}</p>}
            </div>
            {action && <div className="shrink-0">{action}</div>}
        </div>
    );
}

export function Surface({ className = "", children, ...rest }) {
    return (
        <div className={`bg-white border border-fleet-border rounded-md ${className}`} {...rest}>
            {children}
        </div>
    );
}

export function Stat({ label, value, hint, accent = "default", testid }) {
    const accents = {
        default: "text-fleet-ink",
        warning: "text-fleet-warning",
        critical: "text-fleet-critical",
        healthy: "text-fleet-healthy",
        info: "text-fleet-info",
    };
    return (
        <Surface className="p-5" data-testid={testid}>
            <div className="label-mono">{label}</div>
            <div className={`font-mono text-3xl font-semibold tabular mt-2 ${accents[accent] || accents.default}`}>
                {value}
            </div>
            {hint && <div className="text-xs text-fleet-inkSoft mt-1.5">{hint}</div>}
        </Surface>
    );
}

const STATUS_STYLES = {
    healthy: "bg-emerald-50 text-emerald-700 border-emerald-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    open: "bg-blue-50 text-blue-700 border-blue-200",
    in_progress: "bg-amber-50 text-amber-700 border-amber-200",
    in_service: "bg-amber-50 text-amber-700 border-amber-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    done: "bg-slate-100 text-slate-700 border-slate-200",
    completed: "bg-slate-100 text-slate-700 border-slate-200",
    critical: "bg-rose-50 text-rose-700 border-rose-200",
    high: "bg-rose-50 text-rose-700 border-rose-200",
    cancelled: "bg-slate-50 text-slate-500 border-slate-200",
    decommissioned: "bg-slate-50 text-slate-500 border-slate-200",
    scrapped: "bg-slate-50 text-slate-500 border-slate-200",
    fitted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    in_stock: "bg-blue-50 text-blue-700 border-blue-200",
    retreaded: "bg-violet-50 text-violet-700 border-violet-200",
    preventive: "bg-blue-50 text-blue-700 border-blue-200",
    breakdown: "bg-rose-50 text-rose-700 border-rose-200",
    inspection: "bg-slate-100 text-slate-700 border-slate-200",
    medium: "bg-blue-50 text-blue-700 border-blue-200",
    low: "bg-slate-100 text-slate-600 border-slate-200",
    idle: "bg-slate-100 text-slate-600 border-slate-200",
};

export function Badge({ children, variant = "in_stock", className = "", ...rest }) {
    const style = STATUS_STYLES[variant] || STATUS_STYLES.in_stock;
    return (
        <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium font-mono uppercase tracking-wider border ${style} ${className}`}
            {...rest}
        >
            {children}
        </span>
    );
}

export function Button({ children, variant = "primary", size = "md", className = "", ...rest }) {
    const variants = {
        primary: "bg-fleet-ink text-white hover:bg-slate-800",
        secondary: "bg-white border border-fleet-border text-fleet-ink hover:border-slate-400",
        ghost: "bg-transparent text-fleet-inkSoft hover:bg-slate-50 hover:text-fleet-ink",
        danger: "bg-white border border-rose-200 text-fleet-critical hover:bg-rose-50",
    };
    const sizes = {
        sm: "text-xs px-2.5 py-1.5",
        md: "text-sm px-3.5 py-2",
        lg: "text-sm px-4 py-2.5",
    };
    return (
        <button
            className={`inline-flex items-center gap-1.5 rounded-md font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${variants[variant]} ${sizes[size]} ${className}`}
            {...rest}
        >
            {children}
        </button>
    );
}

export function Input({ label, hint, className = "", ...rest }) {
    return (
        <div className={className}>
            {label && <label className="label-mono block mb-1.5">{label}</label>}
            <input
                className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fleet-ink focus:border-fleet-ink"
                {...rest}
            />
            {hint && <div className="text-xs text-fleet-inkMuted mt-1">{hint}</div>}
        </div>
    );
}

export function Select({ label, options = [], className = "", ...rest }) {
    return (
        <div className={className}>
            {label && <label className="label-mono block mb-1.5">{label}</label>}
            <select
                className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-fleet-ink focus:border-fleet-ink"
                {...rest}
            >
                {options.map((o) =>
                    typeof o === "string" ? (
                        <option key={o} value={o}>
                            {o}
                        </option>
                    ) : (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    )
                )}
            </select>
        </div>
    );
}

export function Modal({ open, onClose, title, children, footer, size = "md" }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);
    if (!open) return null;
    const sizes = { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-fleet-ink/40" onClick={onClose}>
            <div
                className={`bg-white border border-fleet-border rounded-md w-full ${sizes[size]} max-h-[90vh] flex flex-col animate-fade-in`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-fleet-border flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold">{title}</h3>
                    <button onClick={onClose} className="text-fleet-inkSoft hover:text-fleet-ink" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5 overflow-y-auto flex-1">{children}</div>
                {footer && <div className="px-5 py-3 border-t border-fleet-border bg-slate-50 flex justify-end gap-2">{footer}</div>}
            </div>
        </div>
    );
}

export function EmptyState({ title, description, action, icon: Icon }) {
    return (
        <div className="text-center py-16 px-6">
            {Icon && (
                <div className="mx-auto h-12 w-12 rounded-md border border-fleet-border bg-slate-50 flex items-center justify-center text-fleet-inkMuted mb-4">
                    <Icon size={22} />
                </div>
            )}
            <div className="font-display text-lg font-medium">{title}</div>
            {description && <p className="text-sm text-fleet-inkSoft mt-1 max-w-sm mx-auto">{description}</p>}
            {action && <div className="mt-5">{action}</div>}
        </div>
    );
}

export function NewButton({ onClick, label = "New", testid }) {
    return (
        <Button onClick={onClick} variant="primary" data-testid={testid || "new-button"} data-action="write">
            <Plus size={16} weight="bold" />
            {label}
        </Button>
    );
}
